import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { parseTerritoryAppOnboardingParseTerritoryPost } from '@/client/sdk.gen'
import type { Region } from '@/hooks/use-onboarding'
import { useOnboardingStepViewed } from '@/hooks/use-analytics'
import { useOnboarding } from '@/hooks/use-onboarding'
import { StepLayout } from './-components/step-layout'
import { TerritoryMap } from './-components/territory-map'
import { useRegionToggle } from './-components/use-region-toggle'
import { RegionBadgeList } from './-components/region-badge-list'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { Loader2, Sparkles } from 'lucide-react'

export const Route = createFileRoute('/onboarding/company-territory')({
  component: CompanyTerritoryStep,
})

type NlpFeedback
  = | { kind: 'added', count: number }
    | { kind: 'already-in-list' }
    | { kind: 'no-match' }
    | { kind: 'error', message: string }

function CompanyTerritoryStep() {
  useOnboardingStepViewed('company_territory')
  const navigate = useNavigate()
  const { companyTerritory, updateState } = useOnboarding()
  const { regions, setRegions, toggleRegion } = useRegionToggle(companyTerritory)
  const [nlpInput, setNlpInput] = useState('')
  const [nlpFeedback, setNlpFeedback] = useState<NlpFeedback | undefined>()

  const { mutateAsync: parseTerritory, isPending } = useMutation({
    mutationFn: async (body: { text: string }) => {
      const { data, error } = await parseTerritoryAppOnboardingParseTerritoryPost({
        body,
      })
      if (error || !data)
        throw new Error('Failed to parse territory')
      return data
    },
  })

  const handleNlpSubmit = async () => {
    const trimmed = nlpInput.trim()
    if (!trimmed)
      return
    setNlpFeedback(undefined)
    try {
      const result = await parseTerritory({ text: trimmed })
      const parsed = (result?.regions as Region[] | undefined) ?? []
      if (parsed.length === 0) {
        setNlpFeedback({ kind: 'no-match' })
        return
      }
      let addedCount = 0
      setRegions((prev) => {
        const existing = new Set(prev.map(r => r.code))
        const newRegions = parsed.filter(r => !existing.has(r.code))
        addedCount = newRegions.length
        return [...prev, ...newRegions]
      })
      setNlpFeedback(
        addedCount === 0
          ? { kind: 'already-in-list' }
          : { kind: 'added', count: addedCount },
      )
      setNlpInput('')
    }
    catch (error) {
      setNlpFeedback({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Failed to parse territory',
      })
    }
  }

  const handleNext = () => {
    updateState({
      companyTerritory: regions,
      currentStep: 4,
    })
    navigate({ to: '/onboarding/claim-territory' })
  }

  const handleBack = () => {
    navigate({ to: '/onboarding/ideal-customer' })
  }

  return (
    <StepLayout
      title="Where do you operate?"
      subtitle="Pick the regions your company sells into — we use this to scope which companies appear in your Market Intelligence Report."
      onNext={handleNext}
      onBack={handleBack}
      nextLabel="Continue"
      nextDisabled={regions.length === 0}
      maxWidth="xl"
    >
      <div className="space-y-6">
        <div className="space-y-3">
          <Label>Regions you operate in</Label>
          <div className="flex gap-2">
            <Input
              value={nlpInput}
              onChange={(e) => {
                setNlpInput(e.target.value)
                if (nlpFeedback)
                  setNlpFeedback(undefined)
              }}
              placeholder='e.g. "Midwest US, Germany, Southern Ontario"'
              onKeyDown={e => e.key === 'Enter' && handleNlpSubmit()}
            />
            <Button
              onClick={handleNlpSubmit}
              disabled={isPending || !nlpInput.trim()}
              title="Parse regions from your description"
              className="gap-1.5"
            >
              {isPending
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Sparkles className="h-4 w-4" />}
              <span>{isPending ? 'Parsing' : 'Parse regions'}</span>
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Or click regions directly on the map below.
          </p>
          {nlpFeedback && (
            <p
              className={cn(
                'text-xs',
                nlpFeedback.kind === 'added' && 'text-emerald-600 dark:text-emerald-400',
                nlpFeedback.kind === 'already-in-list' && 'text-muted-foreground',
                nlpFeedback.kind === 'no-match' && 'text-muted-foreground',
                nlpFeedback.kind === 'error' && 'text-destructive',
              )}
            >
              {nlpFeedback.kind === 'added' && `Added ${nlpFeedback.count} region${nlpFeedback.count === 1 ? '' : 's'} to your territory.`}
              {nlpFeedback.kind === 'already-in-list' && 'Those regions are already in your territory.'}
              {nlpFeedback.kind === 'no-match' && 'We couldn\'t identify any regions in that text. Try naming specific US states, countries, or provinces.'}
              {nlpFeedback.kind === 'error' && nlpFeedback.message}
            </p>
          )}
        </div>

        <div className="flex gap-4">
          {/* Map */}
          <div className="flex-1">
            <TerritoryMap selectedRegions={regions} onToggleRegion={toggleRegion} />
          </div>

          {/* Selected regions sidebar */}
          <div className="w-56 shrink-0 space-y-3">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Company territory
            </p>

            <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
              {regions.length === 0
                ? (
                    <p className="text-xs italic text-muted-foreground">
                      Type above or click regions on the map
                    </p>
                  )
                : (
                    <>
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-xs font-medium text-muted-foreground">
                          {`${regions.length} region${regions.length === 1 ? '' : 's'} selected`}
                        </span>
                      </div>
                      <RegionBadgeList regions={regions} onRemove={toggleRegion} />
                    </>
                  )}
            </div>
          </div>
        </div>
      </div>
    </StepLayout>
  )
}
